import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export const LegalShell = ({ title, children }) => (
  <div className="min-h-screen bg-[#0b0f17] text-slate-200">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 transition-colors font-mono-digit"
        data-testid="back-to-app"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Hit Path
      </Link>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-50">{title}</h1>
      <div className="prose prose-invert prose-slate max-w-none space-y-4 text-sm leading-relaxed">
        {children}
      </div>
      <div className="pt-10 border-t border-slate-800 text-xs text-slate-600 font-mono-digit">
        © {new Date().getFullYear()} The Jersey Fellas LLC · Hit Path™
      </div>
    </div>
  </div>
);

export const Terms = () => (
  <LegalShell title="Terms of Service">
    <p className="text-slate-400">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">1. Who we are</h2>
    <p>Hit Path (&quot;the Service&quot;) is operated by The Jersey Fellas LLC. By using the Service you agree to these Terms. If you do not agree, do not use the Service.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">2. Eligibility</h2>
    <p>You must be at least 18 years old to use Hit Path (21+ in states where that is the legal gambling age, including Nebraska, Louisiana, Arizona, Mississippi, and Iowa). The Service is intended for use in jurisdictions where lottery play is legal.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">3. No prediction, no guarantee</h2>
    <p>Hit Path displays patterns derived from a personal number-workout method. <strong className="text-amber-300">It does not predict lottery outcomes.</strong> Lottery draws are random. No result shown by the Service is a guarantee, a forecast, or financial advice. Do not wager money you cannot afford to lose.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">4. Entertainment only</h2>
    <p>The Service is provided strictly for entertainment and informational purposes. The Jersey Fellas LLC is not affiliated with any state lottery, Mega Millions, Powerball, or any lottery operator.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">5. Account & payment</h2>
    <p>Paid features (if offered) are billed through Stripe. Subscription renewals continue until cancelled. Refunds are at our discretion and governed by applicable consumer-protection law. Contact support@thejerseyfellas.com for refund requests.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">6. Acceptable use</h2>
    <p>You will not scrape, reverse engineer, resell, or redistribute the Service or its outputs. You will not use the Service for any illegal gambling activity.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">7. Disclaimer of warranties</h2>
    <p>The Service is provided &quot;AS IS&quot; without warranty of any kind, express or implied. To the maximum extent permitted by law, The Jersey Fellas LLC disclaims all warranties including merchantability, fitness for a particular purpose, and non-infringement.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">8. Limitation of liability</h2>
    <p>In no event will The Jersey Fellas LLC be liable for any indirect, incidental, special, consequential, or exemplary damages, including loss of profits, arising from your use of the Service. Our maximum aggregate liability shall not exceed the amount you paid to us in the twelve months preceding the claim.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">9. Responsible play</h2>
    <p>If gambling is affecting your life, call <span className="text-amber-400 font-mono-digit">1-800-GAMBLER</span> or visit <a className="text-amber-300 underline" href="https://www.ncpgambling.org" target="_blank" rel="noreferrer">ncpgambling.org</a>.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">10. Changes</h2>
    <p>We may update these Terms. Material changes will be posted on this page with a new &quot;Last updated&quot; date.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">11. Contact</h2>
    <p>Questions? Email <a className="text-amber-300" href="mailto:support@thejerseyfellas.com">support@thejerseyfellas.com</a>.</p>
  </LegalShell>
);

export const Privacy = () => (
  <LegalShell title="Privacy Policy">
    <p className="text-slate-400">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">What we collect</h2>
    <p>Workouts you save (seed number, date, starred picks, actual draws you mark) are stored in our database. We may collect anonymous usage analytics (page views, interactions) to improve the Service.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">What we do NOT collect</h2>
    <p>We do not sell your data. We do not collect lottery-ticket purchase information, financial account numbers, or Social Security numbers through this app.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">Payments</h2>
    <p>If you upgrade to a paid plan, Stripe processes your payment. We never see or store your full card number. See <a className="text-amber-300 underline" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a>.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">Cookies & analytics</h2>
    <p>We use essential cookies for session state and a privacy-friendly analytics tool to understand usage. You can block cookies in your browser; some features may stop working.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">Your rights</h2>
    <p>You can request a copy of your data or deletion of your account at any time by emailing <a className="text-amber-300" href="mailto:privacy@thejerseyfellas.com">privacy@thejerseyfellas.com</a>.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">Children</h2>
    <p>Hit Path is not intended for anyone under 18. We do not knowingly collect data from children.</p>

    <h2 className="font-heading text-xl font-bold text-slate-100 mt-6">Contact</h2>
    <p>Email <a className="text-amber-300" href="mailto:privacy@thejerseyfellas.com">privacy@thejerseyfellas.com</a>.</p>
  </LegalShell>
);
